import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  SLAB_SURFACE_POSITION_GROUPING_TYPE,
  buildSlabSurfaceGuidToBeamGuid,
  representativePositionForElement,
} from '@domainUtils'
import { elementTypeToPositionGroupingType } from '@editorUtils'
import { AxiosError } from 'axios'
import { FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useResultsStore } from '@editorStores'
import { useElementTypes } from '@editorHooks'
import { savePositionGrouping } from '@mutations'
import { buildErrorMessage } from 'src/constants'

const EXPORT_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'Exportiert' },
  { value: false, label: 'Nicht exportiert' },
]

interface Props {
  elementGuids: string[]
}

const MultiEditExport = ({ elementGuids }: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const elementTypes = useElementTypes()

  const wallRipsPositionGrouping = useResultsStore(state => state.wallRipsPositionGrouping)
  const wallLintelsPositionGrouping = useResultsStore(state => state.wallLintelsPositionGrouping)
  const slabBeamsPositionGrouping = useResultsStore(state => state.slabBeamsPositionGrouping)
  const roofSlabBeamsPositionGrouping = useResultsStore(
    state => state.roofSlabBeamsPositionGrouping,
  )
  const beamsPositionGrouping = useResultsStore(state => state.beamsPositionGrouping)
  const purlinsPositionGrouping = useResultsStore(state => state.purlinsPositionGrouping)
  const columnsPositionGrouping = useResultsStore(state => state.columnsPositionGrouping)

  const setWallRipsPositionGrouping = useResultsStore(state => state.setWallRipsPositionGrouping)
  const setWallLintelsPositionGrouping = useResultsStore(
    state => state.setWallLintelsPositionGrouping,
  )
  const setSlabBeamsPositionGrouping = useResultsStore(state => state.setSlabBeamsPositionGrouping)
  const setRoofSlabBeamsPositionGrouping = useResultsStore(
    state => state.setRoofSlabBeamsPositionGrouping,
  )
  const setBeamsPositionGrouping = useResultsStore(state => state.setBeamsPositionGrouping)
  const setPurlinsPositionGrouping = useResultsStore(state => state.setPurlinsPositionGrouping)
  const setColumnsPositionGrouping = useResultsStore(state => state.setColumnsPositionGrouping)

  const groupingByType = useMemo(
    () => ({
      'wall-rips': wallRipsPositionGrouping,
      'wall-lintels': wallLintelsPositionGrouping,
      'slab-beams': slabBeamsPositionGrouping,
      'roof-slab-beams': roofSlabBeamsPositionGrouping,
      beams: beamsPositionGrouping,
      purlins: purlinsPositionGrouping,
      columns: columnsPositionGrouping,
    }),
    [
      wallRipsPositionGrouping,
      wallLintelsPositionGrouping,
      slabBeamsPositionGrouping,
      roofSlabBeamsPositionGrouping,
      beamsPositionGrouping,
      purlinsPositionGrouping,
      columnsPositionGrouping,
    ],
  )

  const settersByType = useMemo(
    () => ({
      'wall-rips': setWallRipsPositionGrouping,
      'wall-lintels': setWallLintelsPositionGrouping,
      'slab-beams': setSlabBeamsPositionGrouping,
      'roof-slab-beams': setRoofSlabBeamsPositionGrouping,
      beams: setBeamsPositionGrouping,
      purlins: setPurlinsPositionGrouping,
      columns: setColumnsPositionGrouping,
    }),
    [
      setWallRipsPositionGrouping,
      setWallLintelsPositionGrouping,
      setSlabBeamsPositionGrouping,
      setRoofSlabBeamsPositionGrouping,
      setBeamsPositionGrouping,
      setPurlinsPositionGrouping,
      setColumnsPositionGrouping,
    ],
  )

  // Decke/Dach surfaces have no position grouping of their own: their exported flag is stored on
  // the bundle of the surface's slab beam, so the selection is resolved to that beam.
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const surfaceGuidToBeamGuid = useMemo(
    () => buildSlabSurfaceGuidToBeamGuid(verticalSlabs, verticalRoofSlabs),
    [verticalSlabs, verticalRoofSlabs],
  )

  /**
   * Group the selection by position grouping type (only types that have grouping data), as the
   * bundle guids whose exported flag they control — the element itself, or, for a slab surface,
   * the representative of the bundle its slab beam belongs to.
   */
  const selectedByPositionType = useMemo(() => {
    const byType: Partial<Record<keyof typeof groupingByType, string[]>> = {}
    for (const guid of elementGuids) {
      const elementType = elementTypes[guid]
      if (!elementType) continue
      const surfacePositionType = SLAB_SURFACE_POSITION_GROUPING_TYPE[elementType]
      const positionType = surfacePositionType ?? elementTypeToPositionGroupingType[elementType]
      const grouping = positionType && groupingByType[positionType]
      if (!positionType || !grouping) continue
      const positionGuid = surfacePositionType
        ? representativePositionForElement(grouping, surfaceGuidToBeamGuid.get(guid))
        : guid
      if (!positionGuid) continue
      if (!byType[positionType]) byType[positionType] = []
      byType[positionType]!.push(positionGuid)
    }
    return byType
  }, [elementGuids, elementTypes, groupingByType, surfaceGuidToBeamGuid])

  const canSubmit = useMemo(() => {
    return Object.keys(selectedByPositionType).length > 0
  }, [selectedByPositionType])

  const [exported, setExported] = useState<boolean>(true)

  const { mutate: submitExport, isLoading } = useMutation(
    async (value: boolean) => {
      const positionTypes = Object.keys(selectedByPositionType) as (keyof typeof groupingByType)[]
      for (const positionType of positionTypes) {
        const grouping = groupingByType[positionType]
        const guids = selectedByPositionType[positionType]
        if (!grouping || !guids?.length) continue
        const guidSet = new Set(guids)
        const newBundles = grouping.map(bundle =>
          guidSet.has(bundle.representative_position) ? { ...bundle, exported: value } : bundle,
        )
        const data = await savePositionGrouping.request(
          projectId as string,
          positionType,
          newBundles,
        )
        settersByType[positionType](data)
      }
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Export-Status für alle ausgewählten Bauteile erfolgreich übernommen', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Export-Status'), {
          variant: 'error',
        })
      },
    },
  )

  return (
    <Stack direction="column" spacing={2} width="100%">
      <Stack direction="row" spacing={1} width="100%">
        <FormControl size="small" fullWidth>
          <InputLabel id="multi-edit-export-label">Export-Status</InputLabel>
          <Select
            labelId="multi-edit-export-label"
            value={exported ? 'true' : 'false'}
            label="Export-Status"
            onChange={e => setExported(e.target.value === 'true')}
            data-cy="multi-edit-export-select"
            sx={{
              '& .MuiSelect-select': { padding: '8px 14px' },
            }}
          >
            {EXPORT_OPTIONS.map(opt => (
              <MenuItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SaveButton
          size="small"
          loading={isLoading}
          disabled={!canSubmit}
          onClick={() => submitExport(exported)}
          data-cy="multi-edit-export-submit"
          sx={{ padding: '8px 14px' }}
        >
          Speichern
        </SaveButton>
      </Stack>
    </Stack>
  )
}

export default MultiEditExport
